import {
  type VisualInsetRatios,
  geometricToVisualRect,
  visualToGeometricRect,
} from '@/lib/shapes/shape-visual-bounds'
import type { GridSnapLiveRect } from './types/grid-snap-live-rect'
import type { GridSnapBias } from './types/grid-snap-bias'

/** Closest point on [origin, max] to `value`; used to pick a page origin when outside live areas. */
function nearestLiveRectOriginForAxis(
  value: number,
  axis: 'x' | 'y',
  liveRects: GridSnapLiveRect[]
): number {
  if (liveRects.length === 0) {
    return 0
  }

  let bestOrigin = axis === 'x' ? liveRects[0].x : liveRects[0].y
  let bestDist = Infinity

  for (const r of liveRects) {
    const origin = axis === 'x' ? r.x : r.y
    const extent = axis === 'x' ? r.width : r.height
    const max = origin + extent
    const clamped = Math.min(max, Math.max(origin, value))
    const d = Math.abs(value - clamped)

    if (d < bestDist) {
      bestDist = d
      bestOrigin = origin
    }
  }

  return bestOrigin
}

function snapToStepFromOrigin(
  value: number,
  s: number,
  origin: number,
  bias: GridSnapBias
): number {
  const t = (value - origin) / s
  const k = bias === 'floor' ? Math.floor(t) : bias === 'ceil' ? Math.ceil(t) : Math.round(t)

  return k * s + origin
}

/**
 * Choose one live rect so horizontal and vertical snap share the same grid origin (real grid
 * corners). Per-axis {@link snapScalarToGridAxis} can pick different rects for x vs y when the
 * reference point sits on a boundary or outside pages, which misaligns the two coordinates.
 */
function resolveLiveRectForGridCorner(
  px: number,
  py: number,
  liveRects: GridSnapLiveRect[]
): GridSnapLiveRect | null {
  if (liveRects.length === 0) {
    return null
  }

  for (const r of liveRects) {
    const x2 = r.x + r.width
    const y2 = r.y + r.height

    if (px >= r.x && px <= x2 && py >= r.y && py <= y2) {
      return r
    }
  }

  let best: GridSnapLiveRect | null = null
  let bestD = Infinity

  for (const r of liveRects) {
    const x2 = r.x + r.width
    const y2 = r.y + r.height
    const cx = Math.min(x2, Math.max(r.x, px))
    const cy = Math.min(y2, Math.max(r.y, py))
    const d = (px - cx) ** 2 + (py - cy) ** 2

    if (d < bestD) {
      bestD = d
      best = r
    }
  }

  return best
}

/**
 * Snap `(px, py)` to the nearest intersection of the document grid (same page origin for both axes).
 */
export function snapPointToNearestGridCorner(
  px: number,
  py: number,
  step: number,
  liveRects: GridSnapLiveRect[]
): { x: number; y: number } {
  const s = Math.max(1, Math.round(Number(step)) || 1)
  const rect = resolveLiveRectForGridCorner(px, py, liveRects)

  if (!rect) {
    return {
      x: snapToStepFromOrigin(px, s, 0, 'nearest'),
      y: snapToStepFromOrigin(py, s, 0, 'nearest'),
    }
  }

  return {
    x: snapToStepFromOrigin(px, s, rect.x, 'nearest'),
    y: snapToStepFromOrigin(py, s, rect.y, 'nearest'),
  }
}

/**
 * Snap a single axis value to the document grid. When `value` lies inside a live rect,
 * snapping uses that rect's origin (same as the CSS repeating grid). Otherwise uses
 * the nearest live rect's origin so phase matches the drawn grid (not document 0).
 *
 * `bias`: `floor` = next grid line toward smaller x/y (e.g. line “above” for y); `ceil` = toward larger;
 * `nearest` = round (default).
 */
export function snapScalarToGridAxis(
  value: number,
  step: number,
  axis: 'x' | 'y',
  liveRects: GridSnapLiveRect[],
  bias: GridSnapBias = 'nearest'
): number {
  const s = Math.max(1, Math.round(Number(step)) || 1)

  if (liveRects.length === 0) {
    return snapToStepFromOrigin(value, s, 0, bias)
  }

  for (const r of liveRects) {
    const origin = axis === 'x' ? r.x : r.y
    const extent = axis === 'x' ? r.width : r.height
    const max = origin + extent

    if (value >= origin && value <= max) {
      return snapToStepFromOrigin(value, s, origin, bias)
    }
  }

  const fallbackOrigin = nearestLiveRectOriginForAxis(value, axis, liveRects)

  return snapToStepFromOrigin(value, s, fallbackOrigin, bias)
}

/**
 * Snap the resizing edge(s) of a bounding box to the document grid.
 * Only the edge(s) being dragged (determined by `direction`) are snapped;
 * the opposite edges remain fixed so the shape grows/shrinks toward the grid.
 *
 * direction uses the same 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' handles
 * as UnifiedSelectionResizeHandle.
 */
export function snapResizeBoundsToGrid(
  bounds: { x: number; y: number; width: number; height: number },
  direction: string,
  step: number,
  liveRects: GridSnapLiveRect[],
  minDimension = 1
): { x: number; y: number; width: number; height: number } {
  let { x, y, width, height } = bounds

  if (direction.includes('e')) {
    const snappedRight = snapScalarToGridAxis(x + width, step, 'x', liveRects)

    width = Math.max(minDimension, snappedRight - x)
  } else if (direction.includes('w')) {
    const snappedLeft = snapScalarToGridAxis(x, step, 'x', liveRects)
    const dx = snappedLeft - x

    x = snappedLeft
    width = Math.max(minDimension, width - dx)
  }

  if (direction.includes('s')) {
    const snappedBottom = snapScalarToGridAxis(y + height, step, 'y', liveRects)

    height = Math.max(minDimension, snappedBottom - y)
  } else if (direction.includes('n')) {
    const snappedTop = snapScalarToGridAxis(y, step, 'y', liveRects)
    const dy = snappedTop - y

    y = snappedTop
    height = Math.max(minDimension, height - dy)
  }

  return { x, y, width, height }
}

/**
 * {@link snapResizeBoundsToGrid} for a shape whose resize handle rides its **visual** box while its
 * persisted rect is the **geometric** one.
 *
 * The user aims a gridline with the edge they can see, so that is the edge that has to land on it.
 * Snapping the geometric rect instead leaves the visible edge short of the line by the shape's own
 * inset — a triangle's base sits at 75% of its generator box, so the generator edge would meet the
 * gridline while the drawn base stopped a quarter of the height above it.
 *
 * So: convert to the visual box, snap the dragged edge there, convert back. Identity ratios (a
 * rectangle) reduce this to `snapResizeBoundsToGrid` exactly. `minDimension` floors the visual
 * extent, matching `computeSingleObjectOrientedResize`'s own convention.
 *
 * Both rects are unrotated canvas-space boxes — this snaps a rotated shape's axis-aligned
 * `x/y/width/height` just as the geometric version always has, rather than its rotated extent.
 */
export function snapVisualResizeBoundsToGrid(
  geometric: { x: number; y: number; width: number; height: number },
  ratios: VisualInsetRatios,
  direction: string,
  step: number,
  liveRects: GridSnapLiveRect[],
  minDimension = 1
): { x: number; y: number; width: number; height: number } {
  return visualToGeometricRect(
    snapResizeBoundsToGrid(
      geometricToVisualRect(geometric, ratios),
      direction,
      step,
      liveRects,
      minDimension
    ),
    ratios
  )
}

/** Adjust creation `(x,y)` so the path registration (placed top-left) lands on the document grid — not stroke ink. */
export function snapShapeCreationPosition(
  createX: number,
  createY: number,
  opts: {
    width: number
    height: number
    anchor?: 'center' | 'top-left' | 'bottom-right'
    anchorPoint?: { x: number; y: number }
  },
  step: number,
  liveRects: GridSnapLiveRect[]
): { x: number; y: number } {
  const anchor = opts.anchor ?? 'center'
  const placedX = opts.anchorPoint
    ? createX - opts.anchorPoint.x
    : anchor === 'top-left'
      ? createX
      : createX - opts.width / 2
  const placedY = opts.anchorPoint
    ? createY - opts.anchorPoint.y
    : anchor === 'top-left'
      ? createY
      : createY - opts.height / 2
  const sx = snapScalarToGridAxis(placedX, step, 'x', liveRects)
  const sy = snapScalarToGridAxis(placedY, step, 'y', liveRects)

  return {
    x: createX + (sx - placedX),
    y: createY + (sy - placedY),
  }
}
